import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import styles from "./legacyInteractiveGenerator.module.css";
import CodePreviewPanel from "../Interactives/CodePreviewPanel";
import { systemPromptToGenerateInteractives } from "../Interactives/prompts";
import { streamAnthropicMessages } from "../Interactives/anthropic";
import { createRoot } from "react-dom/client";
import ReactDOM from "react-dom";
import { animated, useSpring } from "react-spring";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";
import "prismjs/themes/prism.css";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import * as Babel from "@babel/standalone";
import styled from "styled-components";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  Line,
} from "react-simple-maps";
import { scaleQuantile } from "d3-scale";
import { motion, AnimatePresence } from "framer-motion";
import { Tooltip } from "react-tooltip";
import * as LucideIcons from "lucide-react";
import * as Recharts from "recharts";
import html2Canvas from "html2canvas";
import * as THREE from "three";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button as ShadButton } from "../ui/button";
import { Slider } from "../ui/slider";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { injectExternalCss } from "../../utils/inject";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordian";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alertDialog";
import { AspectRatio } from "../ui/aspectRatio";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../ui/breadcrumb";
import { Calendar } from "../ui/calender";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../ui/carousal";
import { Checkbox } from "../ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import { ComboboxDemo } from "../ui/combobox";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "../ui/command";
import {
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuGroup,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuPortal,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from "../ui/contextmenu";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
} from "../ui/drawer";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../ui/dropdownmenu";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "../ui/form";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../ui/hovercard";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "../ui/inputotp";
import {
  Menubar,
  MenubarCheckboxItem,
  MenubarContent,
  MenubarGroup,
  MenubarItem,
  MenubarLabel,
  MenubarMenu,
  MenubarPortal,
  MenubarRadioGroup,
  MenubarRadioItem,
  MenubarSeparator,
  MenubarShortcut,
  MenubarSub,
  MenubarSubContent,
  MenubarSubTrigger,
  MenubarTrigger,
} from "../ui/menubar";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../ui/pagination";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Progress } from "../ui/progress";
import { RadioGroup, RadioGroupItem } from "../ui/radiogroup";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "../ui/resizable";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Separator } from "../ui/seperator";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
} from "../ui/sheet";
import { Skeleton } from "../ui/skeleton";
import { Toaster } from "../ui/sonner";
import { Switch as ShadSwitch } from "../ui/switch";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Textarea } from "../ui/textarea";
import {
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "../ui/toast";
import { Toggle, toggleVariants } from "../ui/toggle";
import { ToggleGroup, ToggleGroupItem } from "../ui/togglegroup";
import { TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import {
  Button,
  CircularProgress,
  Grid,
  Switch,
  TextField,
  Typography,
  Popover as MuiPopover,
  Menu,
  MenuItem,
  Box,
  Stack,
} from "@mui/material";
import { ArrowDropDown, Lock } from "@mui/icons-material";
import p5 from "p5";
import { profJimBrandColors } from "../../theme/variants";
import Moveable, { ElementGuidelineValueOption } from "react-moveable";
import confetti from "canvas-confetti";
import { mathJaxConfig } from "../Interactives/CodeEditorAndRunner";

const MODEL = "claude-3-5-sonnet-20241022";
const MAX_TOKENS = 8192;

const LegacyInteractiveGenerator = () => {
  const [buttonText, setButtonText] = useState("Generate");
  const [refineCount, setRefineCount] = useState(0);
  const [prompt, setPrompt] = useState(
    'Code up a webpage for a hypothetical bank titled "H bank" focused on rural agricultural customers. Emphasize agricultural loans, crop insurance and presence of rural branches.'
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [generatedCode, setGeneratedCode] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const prevCodeRef = useRef("");
  const [activePreview, setActivePreview] = useState("code");
  const [activeTabValue, setActiveTabValue] = useState({
    code: "code",
    preview: "preview",
  });
  const gameContainerRef = useRef(null);
  const rootRef = useRef(null);
  const [generatedComponent, setGeneratedComponent] = useState(null);
  const [output, setOutput] = useState(false);
  const handleError = (error) => {
    setError(error?.message || "Error generating code");
    setIsLoading(false);
    setButtonText("Generate");
  };
  const prevGeneratedCodes = useRef("");
  const [generatedJSX, setGeneratedJSX] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isEmbed, setIsEmbed] = useState(false);
  const [isAddingToLibrary, setIsAddingToLibrary] = useState(false);
  const [addedLibraryUrl, setAddedLibraryUrl] = useState("");
  const [themeColor, setThemeColor] = useState({
    themeBackgroundColor: "#ffffff",
    primaryFont: "Henderson BCG Sans",
    previewbackgroundColor: "#366e56",
  });
  const [retryCountRef, setRetryCountRef] = useState(0);
  const [MAX_RETRIES, setMAX_RETRIES] = useState(3);
  const followupCount = useRef(0);
  const [followupCountState, setFollowupCountState] = useState(0);
  const [inputCode, setInputCode] = useState("");
  const [cssLoaded, setCssLoaded] = useState(false);

  useEffect(() => {
    injectExternalCss({ isLegacy: false }).then(() => {
      setCssLoaded(true);
    });
  }, []);

  const trackEvent = (event, traceId, data) => {
    // analytics.trackEvent(event, {
    //   eventProperties: data,
    //   traceId,
    // });
  };

  function cleanGeneratedCode(code) {
    // Remove any leading or trailing backticks and 'jsx' tag
    code = code.replace(/^```jsx?|```$/g, "").trim();

    const firstIndexOfInteractiveComponent = code.indexOf(
      "const InteractiveElement = () =>"
    );

    if (firstIndexOfInteractiveComponent > -1) {
      code = code.slice(firstIndexOfInteractiveComponent);
    }

    // Count opening and closing braces to ensure balance
    let braceCount = 0;
    let cleanedCode = "";
    for (let char of code) {
      if (char === "{") braceCount++;
      if (char === "}") braceCount--;
      cleanedCode += char;

      // Stop if we've reached a balanced state and encountered the last closing brace
      if (braceCount === 0 && char === "}") break;
    }

    // Ensure the code ends with a single closing brace
    cleanedCode = cleanedCode.replace(/}+$/, "}");

    prevGeneratedCodes.current = cleanedCode;
    setGeneratedJSX(cleanedCode);
    return cleanedCode;
  }

  function executeTranspiledCode(code) {
    const cleanedCode = cleanGeneratedCode(code);
    try {
      const transformedCode = Babel.transform(cleanedCode, {
        presets: ["react"],
      }).code;

      const execFunction = new Function(
        "React",
        "ReactDOM",
        "styled",
        "animated",
        "useSpring",
        "useState",
        "useEffect",
        "useCallback",
        "useMemo",
        "ComposableMap",
        "Geographies",
        "Geography",
        "Marker",
        "scaleQuantile",
        "Tooltip",
        "Button",
        "Line",
        "motion",
        "AnimatePresence",
        "useRef",
        "p5",
        "LucideIcons",
        "Recharts",
        "Card",
        "CardContent",
        "CardHeader",
        "CardTitle",
        "ShadButton",
        "Slider",
        "Input",
        "Label",
        "Badge",
        "Accordion",
        "AccordionContent",
        "AccordionItem",
        "AccordionTrigger",
        "Alert",
        "AlertDescription",
        "AlertTitle",
        "AlertDialog",
        "AlertDialogAction",
        "AlertDialogCancel",
        "AlertDialogContent",
        "AlertDialogDescription",
        "AlertDialogFooter",
        "AlertDialogHeader",
        "AlertDialogOverlay",
        "AlertDialogPortal",
        "AlertDialogTitle",
        "AlertDialogTrigger",
        "AspectRatio",
        "Avatar",
        "AvatarFallback",
        "AvatarImage",
        "Breadcrumb",
        "BreadcrumbEllipsis",
        "BreadcrumbItem",
        "BreadcrumbLink",
        "BreadcrumbList",
        "BreadcrumbPage",
        "BreadcrumbSeparator",
        "Calendar",
        "Carousel",
        "CarouselContent",
        "CarouselItem",
        "CarouselNext",
        "CarouselPrevious",
        "Checkbox",
        "Collapsible",
        "CollapsibleContent",
        "CollapsibleTrigger",
        "ComboboxDemo",
        "Command",
        "CommandDialog",
        "CommandEmpty",
        "CommandGroup",
        "CommandInput",
        "CommandItem",
        "CommandList",
        "CommandSeparator",
        "CommandShortcut",
        "ContextMenu",
        "ContextMenuCheckboxItem",
        "ContextMenuContent",
        "ContextMenuGroup",
        "ContextMenuItem",
        "ContextMenuLabel",
        "ContextMenuPortal",
        "ContextMenuRadioGroup",
        "ContextMenuRadioItem",
        "ContextMenuSeparator",
        "ContextMenuShortcut",
        "ContextMenuSub",
        "ContextMenuSubContent",
        "ContextMenuSubTrigger",
        "ContextMenuTrigger",
        "Dialog",
        "DialogClose",
        "DialogContent",
        "DialogDescription",
        "DialogFooter",
        "DialogHeader",
        "DialogOverlay",
        "DialogPortal",
        "DialogTitle",
        "DialogTrigger",
        "Drawer",
        "DrawerClose",
        "DrawerContent",
        "DrawerDescription",
        "DrawerFooter",
        "DrawerHeader",
        "DrawerOverlay",
        "DrawerPortal",
        "DrawerTitle",
        "DrawerTrigger",
        "DropdownMenu",
        "DropdownMenuCheckboxItem",
        "DropdownMenuContent",
        "DropdownMenuGroup",
        "DropdownMenuItem",
        "DropdownMenuLabel",
        "DropdownMenuPortal",
        "DropdownMenuRadioGroup",
        "DropdownMenuRadioItem",
        "DropdownMenuSeparator",
        "DropdownMenuShortcut",
        "DropdownMenuSub",
        "DropdownMenuSubContent",
        "DropdownMenuSubTrigger",
        "DropdownMenuTrigger",
        "Form",
        "FormControl",
        "FormDescription",
        "FormField",
        "FormItem",
        "FormLabel",
        "FormMessage",
        "useFormField",
        "HoverCard",
        "HoverCardContent",
        "HoverCardTrigger",
        "InputOTP",
        "InputOTPGroup",
        "InputOTPSeparator",
        "InputOTPSlot",
        "Menubar",
        "MenubarCheckboxItem",
        "MenubarContent",
        "MenubarGroup",
        "MenubarItem",
        "MenubarLabel",
        "MenubarMenu",
        "MenubarPortal",
        "MenubarRadioGroup",
        "MenubarRadioItem",
        "MenubarSeparator",
        "MenubarShortcut",
        "MenubarSub",
        "MenubarSubContent",
        "MenubarSubTrigger",
        "MenubarTrigger",
        "Pagination",
        "PaginationContent",
        "PaginationEllipsis",
        "PaginationItem",
        "PaginationLink",
        "PaginationNext",
        "PaginationPrevious",
        "Popover",
        "PopoverContent",
        "PopoverTrigger",
        "Progress",
        "RadioGroup",
        "RadioGroupItem",
        "ResizableHandle",
        "ResizablePanel",
        "ResizablePanelGroup",
        "Select",
        "SelectContent",
        "SelectGroup",
        "SelectItem",
        "SelectLabel",
        "SelectScrollDownButton",
        "SelectScrollUpButton",
        "SelectSeparator",
        "SelectTrigger",
        "SelectValue",
        "Separator",
        "Sheet",
        "SheetClose",
        "SheetContent",
        "SheetDescription",
        "SheetFooter",
        "SheetHeader",
        "SheetOverlay",
        "SheetPortal",
        "SheetTitle",
        "SheetTrigger",
        "Skeleton",
        "Toaster",
        "Switch",
        "Table",
        "TableBody",
        "TableCaption",
        "TableCell",
        "TableFooter",
        "TableHead",
        "TableHeader",
        "TableRow",
        "Tabs",
        "TabsContent",
        "TabsList",
        "TabsTrigger",
        "Textarea",
        "Toast",
        "ToastAction",
        "ToastClose",
        "ToastDescription",
        "ToastProvider",
        "ToastTitle",
        "ToastViewport",
        "Toggle",
        "toggleVariants",
        "ToggleGroup",
        "ToggleGroupItem",
        "TooltipContent",
        "TooltipProvider",
        "TooltipTrigger",
        "html2Canvas",
        "Moveable",
        "MathJax",
        "MathJaxContext",
        "mathJaxConfig",
        "trackEvent",
        "THREE",
        "confetti",
        `${transformedCode}
        return InteractiveElement;`
      );

      setError(null);
      return execFunction(
        React,
        ReactDOM,
        styled,
        animated,
        useSpring,
        useState,
        useEffect,
        useCallback,
        useMemo,
        ComposableMap,
        Geographies,
        Geography,
        Marker,
        scaleQuantile,
        Tooltip,
        ShadButton,
        Line,
        motion,
        AnimatePresence,
        useRef,
        p5,
        LucideIcons,
        Recharts,
        Card,
        CardContent,
        CardHeader,
        CardTitle,
        ShadButton,
        Slider,
        Input,
        Label,
        Badge,
        Accordion,
        AccordionContent,
        AccordionItem,
        AccordionTrigger,
        Alert,
        AlertDescription,
        AlertTitle,
        AlertDialog,
        AlertDialogAction,
        AlertDialogCancel,
        AlertDialogContent,
        AlertDialogDescription,
        AlertDialogFooter,
        AlertDialogHeader,
        AlertDialogOverlay,
        AlertDialogPortal,
        AlertDialogTitle,
        AlertDialogTrigger,
        AspectRatio,
        Avatar,
        AvatarFallback,
        AvatarImage,
        Breadcrumb,
        BreadcrumbEllipsis,
        BreadcrumbItem,
        BreadcrumbLink,
        BreadcrumbList,
        BreadcrumbPage,
        BreadcrumbSeparator,
        Calendar,
        Carousel,
        CarouselContent,
        CarouselItem,
        CarouselNext,
        CarouselPrevious,
        Checkbox,
        Collapsible,
        CollapsibleContent,
        CollapsibleTrigger,
        ComboboxDemo,
        Command,
        CommandDialog,
        CommandEmpty,
        CommandGroup,
        CommandInput,
        CommandItem,
        CommandList,
        CommandSeparator,
        CommandShortcut,
        ContextMenu,
        ContextMenuCheckboxItem,
        ContextMenuContent,
        ContextMenuGroup,
        ContextMenuItem,
        ContextMenuLabel,
        ContextMenuPortal,
        ContextMenuRadioGroup,
        ContextMenuRadioItem,
        ContextMenuSeparator,
        ContextMenuShortcut,
        ContextMenuSub,
        ContextMenuSubContent,
        ContextMenuSubTrigger,
        ContextMenuTrigger,
        Dialog,
        DialogClose,
        DialogContent,
        DialogDescription,
        DialogFooter,
        DialogHeader,
        DialogOverlay,
        DialogPortal,
        DialogTitle,
        DialogTrigger,
        Drawer,
        DrawerClose,
        DrawerContent,
        DrawerDescription,
        DrawerFooter,
        DrawerHeader,
        DrawerOverlay,
        DrawerPortal,
        DrawerTitle,
        DrawerTrigger,
        DropdownMenu,
        DropdownMenuCheckboxItem,
        DropdownMenuContent,
        DropdownMenuGroup,
        DropdownMenuItem,
        DropdownMenuLabel,
        DropdownMenuPortal,
        DropdownMenuRadioGroup,
        DropdownMenuRadioItem,
        DropdownMenuSeparator,
        DropdownMenuShortcut,
        DropdownMenuSub,
        DropdownMenuSubContent,
        DropdownMenuSubTrigger,
        DropdownMenuTrigger,
        Form,
        FormControl,
        FormDescription,
        FormField,
        FormItem,
        FormLabel,
        FormMessage,
        useFormField,
        HoverCard,
        HoverCardContent,
        HoverCardTrigger,
        InputOTP,
        InputOTPGroup,
        InputOTPSeparator,
        InputOTPSlot,
        Menubar,
        MenubarCheckboxItem,
        MenubarContent,
        MenubarGroup,
        MenubarItem,
        MenubarLabel,
        MenubarMenu,
        MenubarPortal,
        MenubarRadioGroup,
        MenubarRadioItem,
        MenubarSeparator,
        MenubarShortcut,
        MenubarSub,
        MenubarSubContent,
        MenubarSubTrigger,
        MenubarTrigger,
        Pagination,
        PaginationContent,
        PaginationEllipsis,
        PaginationItem,
        PaginationLink,
        PaginationNext,
        PaginationPrevious,
        Popover,
        PopoverContent,
        PopoverTrigger,
        Progress,
        RadioGroup,
        RadioGroupItem,
        ResizableHandle,
        ResizablePanel,
        ResizablePanelGroup,
        Select,
        SelectContent,
        SelectGroup,
        SelectItem,
        SelectLabel,
        SelectScrollDownButton,
        SelectScrollUpButton,
        SelectSeparator,
        SelectTrigger,
        SelectValue,
        Separator,
        Sheet,
        SheetClose,
        SheetContent,
        SheetDescription,
        SheetFooter,
        SheetHeader,
        SheetOverlay,
        SheetPortal,
        SheetTitle,
        SheetTrigger,
        Skeleton,
        Toaster,
        ShadSwitch,
        Table,
        TableBody,
        TableCaption,
        TableCell,
        TableFooter,
        TableHead,
        TableHeader,
        TableRow,
        Tabs,
        TabsContent,
        TabsList,
        TabsTrigger,
        Textarea,
        Toast,
        ToastAction,
        ToastClose,
        ToastDescription,
        ToastProvider,
        ToastTitle,
        ToastViewport,
        Toggle,
        toggleVariants,
        ToggleGroup,
        ToggleGroupItem,
        TooltipContent,
        TooltipProvider,
        TooltipTrigger,
        html2Canvas,
        Moveable,
        MathJax,
        MathJaxContext,
        mathJaxConfig,
        trackEvent,
        THREE,
        confetti
      );
    } catch (error) {
      console.error("Error in code transformation or execution:", error);
      throw new Error(`Failed to execute code: ${error.message}`);
    }
  }

  const updatePreview = (code) => {
    try {
      const InteractiveElement = executeTranspiledCode(code);
      if (typeof InteractiveElement !== "function") {
        throw new Error(
          "The generated code did not return a valid React component"
        );
      }
      setOutput(true);
      setGeneratedComponent(() => InteractiveElement);
      setShowPreview(true);
    } catch (evalError) {
      console.error("Error evaluating generated code:", evalError);
      handleError(evalError);
    }
  };

  let currentGeneratedJSX = "";
  const handleGenerate = async () => {
    setIsLoading(true);
    setError("");
    setActivePreview("code");
    setRefineCount(0);
    setGeneratedComponent(null);
    setGeneratedJSX("");
    setButtonText("Generating...");
    setShowPreview(true);
    setGeneratedCode("");
    let streamedCode = "";
    try {
      const messages = [
        { role: "assistant", content: systemPromptToGenerateInteractives },
        {
          role: "user",
          content:
            refineCount === 0
              ? prompt
              : `Modify the given React JSX code based on given instructions and provide only JSX without any comments added. Also make sure to not add any commentary before \"const InteractiveElements = ()\". The JSX output should start from \"InteractiveElement\" component. Also make sure to make no other changes in the code beyond the user instructions.\n##############\nJSX code- \n${prevCodeRef.current}\n##############\nInstruction - \n${prompt}`,
        },
      ];
      await streamAnthropicMessages({
        messages,
        model: MODEL,
        maxTokens: MAX_TOKENS,
        onText: (text) => {
          currentGeneratedJSX += text;
          setGeneratedJSX((prevCode) => prevCode + text);
        },
        onEnd: async () => {
          try {
            followupCount.current += 1;
            setFollowupCountState(followupCount.current);
            if (
              !currentGeneratedJSX.endsWith("}") ||
              !currentGeneratedJSX.endsWith("};")
            ) {
              currentGeneratedJSX += "}";
            }
            prevGeneratedCodes.current = currentGeneratedJSX;

            setTimeout(async () => {
              console.log("currentGeneratedJSX", { code: currentGeneratedJSX });
              await updatePreview(currentGeneratedJSX);
              setShowPreview(true);
              setActivePreview(activeTabValue.preview);
              setInputCode("");
              setIsProcessing(false);
              window.currentGenerationParams = null;
              // If automatic refine is enabled and we are in edit mode, add the game to the library
            }, 500);
          } catch (previewError) {
            console.error("Preview error:", previewError);
            handleError(previewError);
          } finally {
            setIsLoading(false);
            setButtonText("Generate");
            setRefineCount(1);
          }
        },
        onError: (error) => {
          console.error("Stream error:", error);
          handleError(error);
        },
      });
    } catch (err) {
      setError(err?.message || "Error generating code");
      setIsLoading(false);
      setButtonText("Generate");
    }
  };

  const handleRefine = () => {
    handleGenerate();
  };

  const handleNewSession = () => {
    setPrompt("");
    setGeneratedCode("");
    setGeneratedJSX("");
    setGeneratedComponent(null);
    setShowPreview(false);
    setRefineCount(0);
    setButtonText("Generate");
    setError("");
    prevCodeRef.current = "";
  };

  useEffect(() => {
    if (showPreview && generatedComponent && gameContainerRef.current) {
      try {
        if (!rootRef.current) {
          rootRef.current = createRoot(gameContainerRef.current);
        }
        rootRef.current.render(React.createElement(generatedComponent));
      } catch (err) {
        console.log("Error occured: ", err);
      }
    }
    return () => {
      if (rootRef.current) {
        rootRef.current.unmount();
        rootRef.current = null;
      }
    };
  }, [showPreview, generatedComponent]);

  const renderGamePreview = () => {
    try {
      return (
        <>
          <div
            id="game-container"
            ref={gameContainerRef}
            style={{
              display: activePreview === "preview" ? "" : "none",
            }}
          ></div>
        </>
      );
    } catch (err) {
      console.log("Error occured ");
      return (
        <>
          <div>Error occured: {err}</div>
        </>
      );
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.instructions}>
        Enter your desired prompt in the provided field, then click the
        "Generate" button.
      </div>
      <div className={styles.promptBoxWrapper}>
        <textarea
          className={styles.promptBox}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          disabled={isLoading}
        />
      </div>
      <div className={styles.buttonRow}>
        <button
          className={styles.button}
          onClick={handleGenerate}
          disabled={isLoading || !prompt.trim()}
        >
          {isLoading ? "Generating..." : buttonText}
        </button>
        {refineCount > 0 && (
          <button
            className={styles.buttonPrimary}
            onClick={handleNewSession}
            disabled={isLoading}
          >
            + Start New Session
          </button>
        )}
      </div>
      {error && <div style={{ color: "#F91F2F", marginTop: 12 }}>{error}</div>}
      {showPreview && (
        <div style={{ marginTop: "20px" }}>
          <CodePreviewPanel
            error={error}
            retryCountRef={retryCountRef}
            MAX_RETRIES={MAX_RETRIES}
            addedLibraryUrl={addedLibraryUrl}
            themeColor={themeColor}
            profJimBrandColors={profJimBrandColors}
            activePreview={activePreview}
            setActivePreview={setActivePreview}
            activeTabValue={activeTabValue}
            generatedJSX={generatedJSX}
            isEmbed={false}
            isAddingToLibrary={false}
            handleAddNewGameToLibrary={() => {}}
            handleGeneratedCodeChange={() => {}}
            isProcessing={isLoading}
            renderGamePreview={renderGamePreview}
            codeFromUrl={""}
            isEditMode={true}
            hidePublicURLButton={true}
          />
        </div>
      )}
    </div>
  );
};

export default LegacyInteractiveGenerator;
